import { useState } from 'react';
import { checkModalValues } from '@spawnwire/shared';
import { Button, Field, Input, Modal, TextArea } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useStore } from '../../store/store';
import { submitForm } from './componentActions';
import { closeForm, useCommands, type OpenForm } from './slashCommands';

function Form({ form }: { form: OpenForm }) {
  const bot = useStore((s) => s.users[form.botId]);
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(form.modal.fields.map((f) => [f.customId, f.value ?? ''])));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    // The same checks the server makes, so mistakes are caught before a round trip.
    const checked = checkModalValues(form.modal, values);
    if ('error' in checked) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitForm(form.interactionId, checked.values);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => closeForm(form.interactionId)}
      title={form.modal.title}
      description={bot ? `From ${bot.displayName}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={() => closeForm(form.interactionId)}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            Submit
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {form.modal.fields.map((f) => {
          const id = `form-${form.interactionId}-${f.customId}`;
          const set = (v: string) => setValues((cur) => ({ ...cur, [f.customId]: v }));
          return (
            <Field key={f.customId} label={f.required ? f.label : `${f.label} (optional)`} htmlFor={id}>
              {f.style === 'paragraph' ? (
                <TextArea id={id} rows={4} value={values[f.customId]} maxLength={f.maxLength} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} />
              ) : (
                <Input id={id} value={values[f.customId]} maxLength={f.maxLength} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} />
              )}
            </Field>
          );
        })}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        {/* Lets Enter submit a one-line form. */}
        <button type="submit" className="sr-only" tabIndex={-1}>
          Submit
        </button>
      </form>
    </Modal>
  );
}

/** Forms bots opened for me in this channel. */
export function ModalForms({ channelId }: { channelId: string }) {
  const forms = useCommands((s) => s.forms);
  return (
    <>
      {Object.values(forms)
        .filter((f) => f.channelId === channelId)
        .map((f) => (
          <Form key={f.interactionId} form={f} />
        ))}
    </>
  );
}
